import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import QRCode from 'qrcode'
import { tables as tablesApi, config, type Table, type TableQr } from '../lib/api.js'
import { TableMergeModal } from '../components/TableMergeModal.js'
import { Button, Input, Modal, Empty, Spinner, toast } from '../components/ui/index.js'
import { cn } from '../lib/utils.js'
import { useI18n } from '../lib/i18n.js'

export function Tables() {
  const { t } = useI18n()
  const qc = useQueryClient()
  const [generateModal, setGenerateModal] = useState(false)
  const [qrModal, setQrModal] = useState<{ open: boolean; tableId?: string }>({ open: false })
  // #132: união de mesas — mesa de origem do modal
  const [mergeSource, setMergeSource] = useState<Table | null>(null)
  const [printItems, setPrintItems] = useState<Array<{ number: number; dataUrl: string }> | null>(
    null,
  )

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['tables'],
    queryFn: () => tablesApi.list(),
  })
  const { data: cfg } = useQuery({ queryKey: ['config'], queryFn: config.get })

  /** Gera os QRs de todas as mesas ativas e abre o diálogo de impressão. */
  async function printAll() {
    const activeTables = [...list].filter((t) => t.is_active).sort((a, b) => a.number - b.number)
    if (activeTables.length === 0) return
    try {
      const items = await Promise.all(
        activeTables.map(async (t) => {
          const qr = await tablesApi.getQr(t.id)
          const dataUrl = await QRCode.toDataURL(qr.qr_url, {
            width: 512,
            margin: 2,
            color: { dark: '#1a1a2e', light: '#ffffff' },
          })
          return { number: t.number, dataUrl }
        }),
      )
      setPrintItems(items)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('admin.tables.qr_print_err'))
    }
  }

  // Imprimir quando os QRs estiverem prontos; limpar ao fechar o diálogo
  useEffect(() => {
    if (!printItems) return
    const t = window.setTimeout(() => window.print(), 150)
    const cleanup = () => setPrintItems(null)
    window.addEventListener('afterprint', cleanup)
    return () => {
      window.clearTimeout(t)
      window.removeEventListener('afterprint', cleanup)
    }
  }, [printItems])

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )

  const active = list.filter((t) => t.is_active)
  const inactive = list.filter((t) => !t.is_active)

  return (
    <div className="space-y-6">
      {/* Área exclusiva de impressão: 2 QRs por página A4 */}
      {printItems && (
        <div className="hidden print:block fixed inset-0 z-[100] bg-white">
          {printItems.map((item) => (
            <div
              key={item.number}
              className="flex flex-col items-center justify-center gap-6 text-center"
              style={{ width: '100vw', height: '50vh', pageBreakAfter: 'always' }}
            >
              <p className="text-3xl font-bold text-gray-900">
                {t('admin.tables.mesa', { n: String(item.number) })}
              </p>
              <img
                src={item.dataUrl}
                alt={t('admin.tables.qr_alt', { n: String(item.number) })}
                style={{ width: 300, height: 300 }}
              />
              <div>
                <p className="text-lg font-semibold text-gray-800">
                  {cfg?.name ?? t('admin.tables.scan_to_order')}
                </p>
                <p className="text-sm text-gray-500 mt-1">{t('admin.tables.scan_hint')}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-start justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.tables.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('admin.tables.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          {active.length > 0 && (
            <Button
              size="sm"
              variant="secondary"
              data-testid="tables-print-all"
              onClick={() => void printAll()}
            >
              {t('admin.tables.print_all')}
            </Button>
          )}
          <Button
            size="sm"
            data-testid="tables-generate-open"
            onClick={() => setGenerateModal(true)}
          >
            {t('admin.tables.generate_cta')}
          </Button>
        </div>
      </div>

      <div className="print:hidden">
        {list.length === 0 ? (
          <Empty
            title={t('admin.tables.empty')}
            description={t('admin.tables.empty_hint')}
            action={
              <Button
                size="sm"
                data-testid="tables-empty-generate"
                onClick={() => setGenerateModal(true)}
              >
                {t('admin.tables.generate_tables')}
              </Button>
            }
          />
        ) : (
          <>
            {/* Stats */}
            <div className="flex gap-4">
              <div className="bg-emerald-50 rounded-xl px-4 py-3">
                <p className="text-2xl font-bold text-emerald-700">{active.length}</p>
                <p className="text-xs text-emerald-600 font-medium">
                  {t('admin.tables.stat_active')}
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl px-4 py-3">
                <p className="text-2xl font-bold text-gray-500">{inactive.length}</p>
                <p className="text-xs text-gray-500 font-medium">
                  {t('admin.tables.stat_inactive')}
                </p>
              </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {list
                .sort((a, b) => a.number - b.number)
                .map((table) => (
                  <TableCard
                    key={table.id}
                    table={table}
                    onQr={() => setQrModal({ open: true, tableId: table.id })}
                    onMerge={() => setMergeSource(table)}
                    qc={qc}
                  />
                ))}
            </div>
          </>
        )}
      </div>

      <GenerateModal
        open={generateModal}
        onClose={() => setGenerateModal(false)}
        existingCount={list.length}
        qc={qc}
      />

      <QrModal {...qrModal} onClose={() => setQrModal({ open: false })} qc={qc} />

      {mergeSource && (
        <TableMergeModal source={mergeSource} tables={list} onClose={() => setMergeSource(null)} />
      )}
    </div>
  )
}

// ─── TableCard ────────────────────────────────────────────────────────────────

function TableCard({
  table,
  onQr,
  onMerge,
  qc,
}: {
  table: Table
  onQr: () => void
  onMerge: () => void
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const toggleMut = useMutation({
    mutationFn: () => tablesApi.update(table.id, { is_active: !table.is_active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tables'] }),
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div
      data-testid={`tables-card-${table.id}`}
      className={cn(
        'bg-white rounded-xl border shadow-sm p-3 flex flex-col items-center gap-2 transition-opacity',
        !table.is_active && 'opacity-50',
      )}
    >
      <div
        className={cn(
          'w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold',
          table.is_active ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-400',
        )}
      >
        {table.number}
      </div>
      {table.label && (
        <p className="text-xs text-gray-500 text-center truncate w-full">{table.label}</p>
      )}
      <div className="flex flex-col gap-1 w-full">
        <Button
          variant="secondary"
          size="sm"
          className="w-full text-xs"
          data-testid={`tables-qr-${table.id}`}
          onClick={onQr}
          disabled={!table.is_active}
        >
          QR
        </Button>
        {/* #132: unir mesa — só mesa ativa e não unida tem destino a oferecer */}
        <Button
          variant="secondary"
          size="sm"
          className="w-full text-xs"
          data-testid={`tables-merge-${table.id}`}
          onClick={onMerge}
          disabled={!table.is_active || !!table.merged_with_table_id}
        >
          {t('admin.tables.merge')}
        </Button>
        <button
          data-testid={`tables-toggle-${table.id}`}
          onClick={() => toggleMut.mutate()}
          disabled={toggleMut.isPending}
          className="text-xs text-gray-400 hover:text-gray-600 text-center"
        >
          {table.is_active ? t('admin.tables.deactivate') : t('admin.tables.activate')}
        </button>
      </div>
    </div>
  )
}

// ─── GenerateModal ────────────────────────────────────────────────────────────

function GenerateModal({
  open,
  onClose,
  existingCount,
  qc,
}: {
  open: boolean
  onClose: () => void
  existingCount: number
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const [count, setCount] = useState('10')
  const [startFrom, setStartFrom] = useState(String(existingCount + 1))

  const mut = useMutation({
    mutationFn: () => tablesApi.generate(parseInt(count, 10) || 1, parseInt(startFrom, 10) || 1),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['tables'] })
      toast.success(t('admin.tables.created_toast', { count: String(data.length) }))
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin.tables.generate_tables')}
      size="sm"
      footer={
        <>
          <Button
            variant="secondary"
            size="sm"
            data-testid="tables-generate-cancel"
            onClick={onClose}
          >
            {t('admin.common.cancel')}
          </Button>
          <Button
            size="sm"
            loading={mut.isPending}
            data-testid="tables-generate-confirm"
            onClick={() => mut.mutate()}
          >
            {t('admin.tables.generate')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label={t('admin.tables.qty')}
          type="number"
          min="1"
          max="200"
          data-testid="tables-generate-count"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          hint={t('admin.tables.qty_hint')}
        />
        <Input
          label={t('admin.tables.start_from')}
          type="number"
          min="1"
          data-testid="tables-generate-start"
          value={startFrom}
          onChange={(e) => setStartFrom(e.target.value)}
          hint={t('admin.tables.start_from_hint')}
        />
        <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
          {t('admin.tables.preview', {
            count: String(parseInt(count, 10) || 0),
            start: String(parseInt(startFrom, 10) || 1),
          })}
        </div>
      </div>
    </Modal>
  )
}

// ─── QrModal ─────────────────────────────────────────────────────────────────

function QrModal({
  open,
  tableId,
  onClose,
  qc,
}: {
  open: boolean
  tableId?: string
  onClose: () => void
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [qrData, setQrData] = useState<TableQr | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !tableId) return
    setLoading(true)
    tablesApi
      .getQr(tableId)
      .then((data) => {
        setQrData(data)
        setLoading(false)
      })
      .catch((e: Error) => {
        toast.error(e.message)
        setLoading(false)
      })
  }, [open, tableId])

  useEffect(() => {
    if (!qrData || !canvasRef.current) return
    QRCode.toCanvas(canvasRef.current, qrData.qr_url, {
      width: 240,
      margin: 2,
      color: { dark: '#1a1a2e', light: '#ffffff' },
    }).catch(console.error)
  }, [qrData])

  const regenMut = useMutation({
    mutationFn: () => tablesApi.regenerateQr(tableId!),
    onSuccess: (data) => {
      setQrData(data)
      void qc.invalidateQueries({ queryKey: ['tables'] })
      toast.success(t('admin.tables.regen_ok'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function download() {
    const canvas = canvasRef.current
    if (!canvas || !qrData) return
    const link = document.createElement('a')
    link.download = `qr-mesa-${qrData.table_number}.png`
    link.href = canvas.toDataURL('image/png')
    link.click()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={qrData ? t('admin.tables.qr_title', { n: String(qrData.table_number) }) : 'QR Code'}
      size="sm"
    >
      <div className="flex flex-col items-center gap-4 py-2">
        {loading ? (
          <Spinner className="w-8 h-8 my-8" />
        ) : (
          <>
            <canvas ref={canvasRef} className="rounded-xl shadow-sm border border-gray-100" />
            {qrData && (
              <div className="w-full">
                <p className="text-xs text-gray-500 text-center mb-1">{t('admin.tables.qr_url')}</p>
                <div
                  data-testid="tables-qr-url"
                  className="bg-gray-50 rounded-lg px-3 py-2 break-all text-xs text-gray-700 font-mono"
                >
                  {qrData.qr_url}
                </div>
              </div>
            )}
            <div className="flex gap-2 w-full">
              <Button
                variant="secondary"
                size="sm"
                className="flex-1"
                data-testid="tables-qr-regen"
                onClick={() => regenMut.mutate()}
                loading={regenMut.isPending}
              >
                {t('admin.tables.regen')}
              </Button>
              <Button
                size="sm"
                className="flex-1"
                data-testid="tables-qr-download"
                onClick={download}
              >
                {t('admin.tables.download_png')}
              </Button>
            </div>
            <p className="text-xs text-gray-400 text-center">{t('admin.tables.regen_hint')}</p>
          </>
        )}
      </div>
    </Modal>
  )
}
